import { themeAtom } from "../../shared/atoms";
import { ProviderRow } from "../models/uwhc.models";
import { BoolBadge } from "./BoolBadge";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

export function UwhcProvidersTable({ rows }: { rows: ProviderRow[] }) {
  const [q, setQ] = useState("");
  const theme = useAtomValue(themeAtom);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) =>
      [r.COMPANY, r.PLACE, r.ADDITIONAL_INFO].filter(Boolean).some((s) => String(s).toLowerCase().includes(qq)),
    );
  }, [rows, q]);

  return (
    <div className={twMerge("mt-3 overflow-hidden rounded-2xl border", theme.uwhcAssistant.tableBorder)}>
      <div
        className={twMerge(
          "flex items-center gap-2 border-b p-3",
          theme.uwhcAssistant.tableBg,
          theme.uwhcAssistant.tableBorder,
        )}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter providers… (company/place/info)"
          className={twMerge(
            "w-full rounded-xl border px-3 py-2 text-sm focus:outline-none",
            theme.uwhcAssistant.tableInputBg,
            theme.uwhcAssistant.tableInputText,
            theme.uwhcAssistant.tableBorder,
          )}
        />
        <span className="whitespace-nowrap text-xs text-gray-400">
          {filtered.length}/{rows.length}
        </span>
      </div>

      <div
        className={twMerge(
          "overflow-x-auto",
          "[&::-webkit-scrollbar]:h-2.5",
          "[&::-webkit-scrollbar-track]:bg-transparent",
          theme.uwhcAssistant.tableScroll,
        )}
      >
        <table className={twMerge("min-w-full text-sm")}>
          <thead className={twMerge(theme.uwhcAssistant.theadBg, theme.uwhcAssistant.theadText)}>
            <tr>
              {[
                "Company",
                "Place",
                "Screened",
                "Preferred",
                "Vert sides",
                "Flat bottom",
                "Propeller",
                "Sea chest int",
                "Sea chest ext",
                "Rudder",
                "Bow thruster grid",
                "Bow thruster tunnel",
                "Bilge keel",
                "Above waterline",
                "UW insp.",
                "UW repairs",
                "AUS/NZ standards",
                "Brush carts",
                "Polishers",
                "Hand scrapers",
                "HP waterjets",
                "ROV UWHC",
                "ROV UW insp.",
                "HullWiper",
                "Extra Info",
                "Regulations",
                "24/7 service",
                "Photos",
                "Videos",
                "Report",
                "Phone",
                "Email",
              ].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr
                key={i}
                className={twMerge(
                  i % 2 === 0 ? theme.uwhcAssistant.tableRowEven : theme.uwhcAssistant.tableRowOdd,
                  theme.uwhcAssistant.tableText,
                )}
              >
                <td className="px-3 py-2 font-medium">{r.COMPANY ?? ""}</td>
                <td className="px-3 py-2">{r.PLACE ?? ""}</td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.SCREENED} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.PREFERRED} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.VERTICAL_SIDES_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.FLAT_BOTTOM_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.PROPELLER_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.SEA_CHEST_CLEANING_INTERNALLY} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.SEA_CHEST_CLEANING_EXTERNALLY} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.RUDDER_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.BOW_THRUSTER_GRID_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.BOW_THRUSTER_TUNNEL_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.BILGE_KEEL_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.ABOVE_WATERLINE_CLEANING} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.UW_INSPECTIONS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.UW_REPAIRS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.CLEANING_TO_AUS_NZ_STANDARDS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.DIVER_EMPLOYED_BRUSH_CARTS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.DIVER_EMPLOYED_POLISHERS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.HAND_SCRAPERS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.HIGH_PRESSURE_WATERJETS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.ROV_FOR_UWHC} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.ROV_FOR_UW_INSPECTIONS} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.HULLWIPER} />
                </td>
                <td className="w-[260px] px-3 py-2">
                  {r.ADDITIONAL_INFO ? (
                    <details className="w-[260px]">
                      <summary
                        className={twMerge("cursor-pointer select-none hover:underline", theme.uwhcAssistant.details)}
                      >
                        Details
                      </summary>
                      <pre className="mt-1 w-[260px] whitespace-pre-wrap text-xs text-gray-700">
                        {r.ADDITIONAL_INFO}
                      </pre>
                    </details>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="w-[260px] px-3 py-2">
                  {r.REGULATIONS ? (
                    <details className="w-[260px]">
                      <summary
                        className={twMerge("cursor-pointer select-none hover:underline", theme.uwhcAssistant.details)}
                      >
                        Details
                      </summary>
                      <pre className="mt-1 w-[260px] whitespace-pre-wrap text-xs text-gray-700">{r.REGULATIONS}</pre>
                    </details>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.SERVICE_24_7} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.PHOTOS_PROVIDED} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.VIDEOS_PROVIDED} />
                </td>
                <td className="px-3 py-2">
                  <BoolBadge v={r.REPORT_PROVIDED} />
                </td>
                <td className="px-3 py-2">
                  <div className="w-[260px]">{r.PHONE}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="w-[260px]">{r.EMAIL}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
